import { getAuth, clerkClient } from "@clerk/express";
import type { Request, Response, NextFunction } from "express";
import { db } from "@workspace/db";
import { usersTable, companiesTable } from "@workspace/db";
import { eq } from "drizzle-orm";
import { sendMail, welcomeHtml } from "./mailer";

export interface AuthRequest extends Request {
  dbUser?: typeof usersTable.$inferSelect;
  companyId?: number;
}

async function fetchOrProvisionDbUser(
  clerkUserId: string,
  sessionClaims: Record<string, unknown>,
): Promise<typeof usersTable.$inferSelect | null> {
  // Look up existing user first
  const existing = await db
    .select()
    .from(usersTable)
    .where(eq(usersTable.clerkId, clerkUserId))
    .limit(1);

  if (existing[0]) return existing[0];

  // Try session claims first (fast path — works when custom JWT claims are configured)
  let email = (sessionClaims?.email ?? sessionClaims?.primary_email_address) as string | undefined;
  let firstName = (sessionClaims?.first_name ?? sessionClaims?.firstName) as string | undefined;
  let lastName = (sessionClaims?.last_name ?? sessionClaims?.lastName) as string | undefined;

  // Fallback: fetch full user from Clerk API (works without custom JWT config)
  if (!email) {
    try {
      const clerkUser = await clerkClient.users.getUser(clerkUserId);
      email = clerkUser.emailAddresses.find((e) => e.id === clerkUser.primaryEmailAddressId)?.emailAddress
        ?? clerkUser.emailAddresses[0]?.emailAddress;
      firstName = firstName ?? clerkUser.firstName ?? undefined;
      lastName = lastName ?? clerkUser.lastName ?? undefined;
    } catch {
      return null;
    }
  }

  if (!email) return null;

  const inserted = await db
    .insert(usersTable)
    .values({
      clerkId: clerkUserId,
      email,
      firstName: firstName ?? null,
      lastName: lastName ?? null,
      role: "company_user",
      language: "ar",
    })
    .returning();

  // Welcome email — fire-and-forget
  sendMail({
    to: email,
    subject: "مرحباً بك في AI Tender Copilot Saudi | Welcome to AI Tender Copilot Saudi",
    html: welcomeHtml(firstName ?? "", true),
  }).catch(() => {});

  return inserted[0] ?? null;
}

async function ensureCompany(user: typeof usersTable.$inferSelect): Promise<typeof usersTable.$inferSelect> {
  if (user.companyId) return user;
  try {
    const defaultName =
      `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
      user.email.split("@")[0];
    const [created] = await db
      .insert(companiesTable)
      .values({ name: defaultName })
      .returning();
    const [updated] = await db
      .update(usersTable)
      .set({ companyId: created.id, updatedAt: new Date() })
      .where(eq(usersTable.id, user.id))
      .returning();
    return updated ?? { ...user, companyId: created.id };
  } catch {
    return user;
  }
}

export async function requireAuth(req: AuthRequest, res: Response, next: NextFunction) {
  const auth = getAuth(req);
  if (!auth?.userId) {
    return res.status(401).json({ error: "Unauthorized" });
  }

  try {
    // Try to find the user; if not found, JIT-provision them so new sign-ups work immediately
    let user = await db
      .select()
      .from(usersTable)
      .where(eq(usersTable.clerkId, auth.userId))
      .limit(1)
      .then((r) => r[0] ?? null);

    if (!user) {
      user = await fetchOrProvisionDbUser(auth.userId, (auth as any).sessionClaims ?? {});
    }

    if (!user) {
      return res.status(401).json({ error: "Unable to resolve user. Please try again." });
    }

    if (user.isSuspended) {
      return res.status(403).json({ error: "Account suspended" });
    }

    user = await ensureCompany(user);
    req.dbUser = user;
    req.companyId = user.companyId ?? undefined;

    next();
  } catch (err) {
    next(err);
  }
}

export async function requireAdmin(req: AuthRequest, res: Response, next: NextFunction) {
  await requireAuth(req, res, () => {
    if (req.dbUser?.role !== "admin") {
      return res.status(403).json({ error: "Admin access required" });
    }
    next();
  });
}

export async function jitProvisionUser(req: AuthRequest, res: Response, next: NextFunction) {
  const auth = getAuth(req);
  if (!auth?.userId) {
    return res.status(401).json({ error: "Unauthorized" });
  }

  try {
    const user = await fetchOrProvisionDbUser(
      auth.userId,
      (auth as any).sessionClaims ?? {},
    );

    if (!user) {
      return res.status(400).json({ error: "Unable to resolve user email from Clerk" });
    }

    if (user.isSuspended) {
      return res.status(403).json({ error: "Account suspended" });
    }

    req.dbUser = user;
    req.companyId = user.companyId ?? undefined;
    next();
  } catch (err) {
    next(err);
  }
}
